import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, Globe, FileUp, Plus, Trash2, RefreshCw, 
  ChevronDown, ChevronRight, Eye, EyeOff, Check, X,
  Upload, ExternalLink, File, AlertCircle, ShoppingCart, Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function ContextSources({ agentId }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    plain_text: true,
    website: true,
    document: true
  });
  
  // Dialog states
  const [showAddPlainText, setShowAddPlainText] = useState(false);
  const [showAddWebsite, setShowAddWebsite] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [showProducts, setShowProducts] = useState(null); // source_id to show products for
  
  // Form states
  const [plainTextForm, setPlainTextForm] = useState({ name: '', content: '' });
  const [websiteForm, setWebsiteForm] = useState({ name: '', url: '' });
  const [processing, setProcessing] = useState(false);
  const [crawlingSource, setCrawlingSource] = useState(null);
  const [scrapingSource, setScrapingSource] = useState(null);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSources();
  }, [agentId]);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/agents/${agentId}/context-sources`);
      setSources(response.data.sources || []);
    } catch (error) {
      console.error('Failed to fetch context sources:', error);
      toast.error('Failed to load context sources');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getSourcesByType = (type) => {
    return sources.filter(s => s.source_type === type);
  };

  const handleToggleEnabled = async (sourceId, currentEnabled) => {
    try {
      await api.put(`/context-sources/${sourceId}`, {
        is_enabled: !currentEnabled
      });
      setSources(sources.map(s => 
        s.source_id === sourceId ? { ...s, is_enabled: !currentEnabled } : s
      ));
      toast.success(`Context source ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update context source');
    }
  };

  const handleDelete = async (sourceId) => {
    try {
      await api.delete(`/context-sources/${sourceId}`);
      setSources(prevSources => prevSources.filter(s => s.source_id !== sourceId));
      toast.success('Context source deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete context source');
    }
  };

  const handleAddPlainText = async () => {
    if (!plainTextForm.name.trim() || !plainTextForm.content.trim()) {
      toast.error('Name and content are required');
      return;
    }
    setProcessing(true);
    try {
      const response = await api.post(`/agents/${agentId}/context-sources`, {
        source_type: 'plain_text',
        name: plainTextForm.name,
        content: plainTextForm.content
      });
      setSources([...sources, response.data.source]);
      setShowAddPlainText(false);
      setPlainTextForm({ name: '', content: '' });
      toast.success('Plain text context added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add context');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddWebsite = async () => {
    if (!websiteForm.name.trim() || !websiteForm.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    // Validate URL
    try {
      new URL(websiteForm.url);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }
    
    setProcessing(true);
    try {
      // Create the source - URL is stored as context reference (no crawling)
      const response = await api.post(`/agents/${agentId}/context-sources`, {
        source_type: 'website',
        name: websiteForm.name,
        url: websiteForm.url
      });
      const newSource = response.data.source;
      setSources([...sources, newSource]);
      setShowAddWebsite(false);
      setWebsiteForm({ name: '', url: '' });
      toast.success(response.data.message || 'Website added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add website');
    } finally {
      setProcessing(false);
    }
  };

  const handleCrawlWebsite = async (sourceId) => {
    setCrawlingSource(sourceId);
    try {
      const response = await api.post(`/agents/${agentId}/context-sources/crawl-url`, null, {
        params: { source_id: sourceId }
      });
      setSources(sources.map(s => 
        s.source_id === sourceId ? response.data.source : s
      ));
      toast.success('Website re-crawled successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to crawl website');
      fetchSources(); // Refresh to get error status
    } finally {
      setCrawlingSource(null);
    }
  };

  const handleScrapeProducts = async (sourceId) => {
    setScrapingSource(sourceId);
    try {
      const response = await api.post(`/context-sources/${sourceId}/scrape-products`);
      setSources(sources.map(s => 
        s.source_id === sourceId ? response.data.source : s
      ));
      toast.success(response.data.message || 'Products scraped successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scrape products');
      fetchSources(); // Refresh to get error status
    } finally {
      setScrapingSource(null);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setProcessing(true);
    try {
      const response = await api.post(
        `/agents/${agentId}/context-sources/upload-document`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      setSources([...sources, response.data.source]);
      toast.success('Document uploaded and processed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openPreview = (source) => {
    setPreviewContent(source);
    setShowPreview(true);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const SourceCard = ({ source }) => (
    <Card className={`border ${source.is_enabled ? 'border-border' : 'border-slate-200 bg-slate-50'}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-sm truncate">{source.name}</h4>
              {source.source_type === 'website' && source.crawl_status === 'failed' && (
                <Badge variant="destructive" className="text-xs">Crawl Failed</Badge>
              )}
              {source.source_type === 'website' && source.product_scrape_status === 'failed' && (
                <Badge variant="destructive" className="text-xs">Scrape Failed</Badge>
              )}
              {source.source_type === 'website' && source.products?.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  <Package className="w-3 h-3 mr-1" />
                  {source.products.length} products
                </Badge>
              )}
              {!source.is_enabled && (
                <Badge variant="secondary" className="text-xs">Disabled</Badge>
              )}
            </div>
            
            {source.source_type === 'website' && source.url && (
              <a 
                href={source.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
              >
                {source.url.slice(0, 40)}...
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            
            {source.source_type === 'document' && (
              <p className="text-xs text-muted-foreground">
                {source.filename} • {formatFileSize(source.file_size)}
              </p>
            )}
            
            {source.content && (
              <p className="text-xs text-muted-foreground mt-1">
                {source.content.length.toLocaleString()} characters
              </p>
            )}
            
            {source.crawl_error && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {source.crawl_error}
              </p>
            )}
            
            {source.product_scrape_error && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {source.product_scrape_error}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {source.content && (
              <Button 
                type="button"
                variant="ghost" 
                size="icon" 
                onClick={(e) => {
                  e.preventDefault();
                  openPreview(source);
                }}
                title="Preview content"
              >
                <Eye className="w-4 h-4" />
              </Button>
            )}
            
            {source.source_type === 'website' && source.products?.length > 0 && (
              <Button 
                type="button"
                variant="ghost" 
                size="icon" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowProducts(showProducts === source.source_id ? null : source.source_id);
                }}
                title="View products"
              >
                <Package className="w-4 h-4" />
              </Button>
            )}
            
            {source.source_type === 'website' && (
              <>
                <Button 
                  type="button"
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleScrapeProducts(source.source_id);
                  }}
                  disabled={scrapingSource === source.source_id}
                  title="Scrape products"
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                >
                  <ShoppingCart className={`w-4 h-4 ${scrapingSource === source.source_id ? 'animate-pulse' : ''}`} />
                </Button>
                <Button 
                  type="button"
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleCrawlWebsite(source.source_id);
                  }}
                  disabled={crawlingSource === source.source_id}
                  title="Crawl website"
                >
                  <RefreshCw className={`w-4 h-4 ${crawlingSource === source.source_id ? 'animate-spin' : ''}`} />
                </Button>
              </>
            )}
            
            <Switch
              checked={source.is_enabled}
              onCheckedChange={() => handleToggleEnabled(source.source_id, source.is_enabled)}
            />
            
            <Button 
              type="button"
              variant="ghost" 
              size="icon" 
              onClick={() => handleDelete(source.source_id)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              data-testid={`delete-source-${source.source_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Products list expanded view */}
        {source.source_type === 'website' && showProducts === source.source_id && source.products?.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Scraped Products ({source.products.length})
            </h5>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {source.products.map((product, index) => (
                <div key={index} className="flex items-start gap-3 p-2 bg-slate-50 rounded-lg">
                  {product.image_url && (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-12 h-12 object-cover rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{product.name || 'Unknown Product'}</p>
                    {product.price && (
                      <p className="text-sm text-green-600 font-semibold">
                        {product.currency} {product.price}
                      </p>
                    )}
                    {product.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {product.description}
                      </p>
                    )}
                    <div className="flex gap-2 mt-1">
                      {product.sku && (
                        <span className="text-xs text-muted-foreground">SKU: {product.sku}</span>
                      )}
                      {product.availability && (
                        <Badge variant="outline" className="text-xs">
                          {product.availability}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const SectionHeader = ({ type, icon: Icon, title, count, onAdd }) => (
    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 rounded-lg transition-colors">
      <CollapsibleTrigger className="flex items-center gap-3 flex-1">
        {expandedSections[type] ? (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        )}
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div className="text-left">
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">
            {count} source{count !== 1 ? 's' : ''} configured
          </p>
        </div>
      </CollapsibleTrigger>
      <Button 
        type="button"
        variant="outline" 
        size="sm" 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAdd();
        }}
      >
        <Plus className="w-4 h-4 mr-1" />
        Add
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const plainTextSources = getSourcesByType('plain_text');
  const websiteSources = getSourcesByType('website');
  const documentSources = getSourcesByType('document');

  return (
    <div className="space-y-4" data-testid="context-sources">
      {/* Plain Text Section */}
      <Collapsible 
        open={expandedSections.plain_text} 
        onOpenChange={(open) => setExpandedSections(prev => ({...prev, plain_text: open}))}
        className="border rounded-xl bg-white"
      >
        <SectionHeader 
          type="plain_text"
          icon={FileText}
          title="Plain Text"
          count={plainTextSources.length}
          onAdd={() => setShowAddPlainText(true)}
        />
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-3">
            {plainTextSources.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No plain text context added yet
              </p>
            ) : (
              plainTextSources.map(source => (
                <SourceCard key={source.source_id} source={source} />
              ))
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Websites Section */}
      <Collapsible 
        open={expandedSections.website} 
        onOpenChange={(open) => setExpandedSections(prev => ({...prev, website: open}))}
        className="border rounded-xl bg-white"
      >
        <SectionHeader 
          type="website"
          icon={Globe}
          title="Websites"
          count={websiteSources.length}
          onAdd={() => setShowAddWebsite(true)}
        />
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-3">
            {websiteSources.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No websites added yet. Add URLs for AI context reference.
              </p>
            ) : (
              websiteSources.map(source => (
                <SourceCard key={source.source_id} source={source} />
              ))
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Documents Section */}
      <Collapsible 
        open={expandedSections.document} 
        onOpenChange={(open) => setExpandedSections(prev => ({...prev, document: open}))}
        className="border rounded-xl bg-white"
      >
        <SectionHeader 
          type="document"
          icon={FileUp}
          title="Documents"
          count={documentSources.length}
          onAdd={() => fileInputRef.current?.click()}
        />
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-3">
            {documentSources.length === 0 ? (
              <div className="text-center py-6">
                <File className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-3">
                  No documents uploaded yet
                </p>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={(e) => {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }}
                  disabled={processing}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Supported: PDF, Word (.docx), Excel (.xlsx), Text (.txt)
                </p>
              </div>
            ) : (
              <>
                {documentSources.map(source => (
                  <SourceCard key={source.source_id} source={source} />
                ))}
                <Button 
                  type="button"
                  variant="outline" 
                  className="w-full"
                  onClick={(e) => {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }}
                  disabled={processing}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Another Document
                </Button>
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.pdf,.docx,.doc,.xlsx,.xls"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Add Plain Text Dialog */}
      <Dialog open={showAddPlainText} onOpenChange={setShowAddPlainText}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Plain Text Context</DialogTitle>
            <DialogDescription>
              Add custom text content that will be included in the agent's context
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="e.g., Company FAQ, Product Info"
                value={plainTextForm.name}
                onChange={(e) => setPlainTextForm({ ...plainTextForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                placeholder="Enter the context information..."
                value={plainTextForm.content}
                onChange={(e) => setPlainTextForm({ ...plainTextForm, content: e.target.value })}
                className="min-h-[200px]"
              />
              <p className="text-xs text-muted-foreground">
                {plainTextForm.content.length.toLocaleString()} characters
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPlainText(false)}>Cancel</Button>
            <Button onClick={handleAddPlainText} disabled={processing}>
              {processing ? 'Adding...' : 'Add Context'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Website Dialog */}
      <Dialog open={showAddWebsite} onOpenChange={setShowAddWebsite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Website Context</DialogTitle>
            <DialogDescription>
              Enter a website URL to use as context for AI responses
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="e.g., Homepage, About Page"
                value={websiteForm.name}
                onChange={(e) => setWebsiteForm({ ...websiteForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                type="url"
                placeholder="https://example.com/about"
                value={websiteForm.url}
                onChange={(e) => setWebsiteForm({ ...websiteForm, url: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                The URL will be stored as a reference for the AI assistant
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddWebsite(false)}>Cancel</Button>
            <Button onClick={handleAddWebsite} disabled={processing}>
              {processing ? 'Adding...' : 'Add Website'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Content Preview: {previewContent?.name}</DialogTitle>
            <DialogDescription>
              {previewContent?.source_type === 'website' && previewContent?.url && (
                <a href={previewContent.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {previewContent.url}
                </a>
              )}
              {previewContent?.source_type === 'document' && previewContent?.filename && (
                <span>{previewContent.filename} • {formatFileSize(previewContent.file_size)}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-slate-50 rounded-lg p-4 max-h-[50vh] overflow-y-auto">
              <pre className="text-sm whitespace-pre-wrap font-mono">
                {previewContent?.content || 'No content available'}
              </pre>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {previewContent?.content?.length?.toLocaleString() || 0} characters
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
